import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Container,
  useTheme,
  Button,
  Skeleton,
  Grid,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Helmet } from "react-helmet-async";
import { clearRebalancer } from "../../Redux/Reducers/rebalancerSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectActiveRequests } from "../../Redux/Reducers/selectors";
import RebalancerModules from "./RebalancerModules";
import SwapStep1 from "./Steps/Step1/SwapStep1";
import SwapStep2 from "./Steps/Step2/SwapStep2";
import SwapStep3 from "./Steps/Step3/SwapStep3";
import FundStep1 from "./Steps/Step1/FundStep1";
import FundStep2 from "./Steps/Step2/FundStep2";
import FundStep3 from "./Steps/Step3/FundStep3";
import FundStep4 from "./Steps/Step4/FundStep4";
import FundStep5 from "./Steps/Step5/FundStep5";
import FundStep6 from "./Steps/Step6/FundStep6";

const Rebalancer = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [showRestartButton, setShowRestartButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [rebalanceType, setRebalanceType] = useState("specific");

  // RebalancerSteps state
  const [rebalancedFunds, setSelectedRebalancedFunds] = useState([]);
  const [rebalancedStrategies, setRebalancedStrategies] = useState([]);
  const [selectedSwapContract, setSelectedSwapContract] = useState([]);
  const [executionStyles, setExecutionStyles] = useState({});
  const [deltaRebalancer, setDeltaRebalancer] = useState(0);
  const [deltaValue, setDeltaValue] = useState(20);
  const [driftValue, setDriftValue] = useState(5);

  // Get active requests to check for loading states
  const activeRequests = useSelector(selectActiveRequests);

  // Check if any rebalancer-related loading is happening
  const rebalancerLoadingPatterns = [
    "rebalancer_check_blacklist",
    "create_rebalancer_items",
    "create_fund_rebalancer_trades",
    "create_rebalancer_crossing_orders",
    "rebalancer_locates",
    "rebalance_swaps",
  ];

  const isRebalancerLoading = activeRequests.some((request) =>
    rebalancerLoadingPatterns.some((pattern) => request.includes(pattern))
  );

  useEffect(() => {
    dispatch(clearRebalancer());
  }, []);

  // Show restart button when step changes
  useEffect(() => {
    if (currentStep > 0) {
      setShowRestartButton(true);
    }
  }, [currentStep]);

  const handleBackToStart = () => {
    setCurrentStep(0);
    setSelectedRebalancedFunds([]);
    setRebalancedStrategies([]);
    setSelectedSwapContract([]);
    setRebalanceType("specific");
    setExecutionStyles({});
    setDeltaRebalancer(0);
    setDeltaValue(20);
    setDriftValue(5);
  };

  const handleRestart = () => {
    setIsLoading(true);
    // Reset the rebalancer state
    dispatch(clearRebalancer());
    // Reset all state
    handleBackToStart();
    setShowRestartButton(false);
    // Simulate loading time for better UX
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const getStepInfo = (step, type) => {
    if (type === "swap") {
      switch (step) {
        case 12: return { number: 1, name: "Select Swap Contracts" };
        case 1: return { number: 1, name: "Review Execution Styles" };
        case 2: return { number: 2, name: "Review Validated Details" };
        case 3: return { number: 3, name: "Submit Swap Contracts" };
        default: return { number: 0, name: "Select Rebalance Type" };
      }
    } else if (type === "all") {
      switch (step) {
        case 0: return { number: 0, name: "Select Rebalance Type" };
        case 2: return { number: 1, name: "Rebalance Preview" };
        case 6: return { number: 2, name: "Final Trades" };
        default: return { number: 0, name: "Select Rebalance Type" };
      }
    } else if (type === "specific") {
      switch (step) {
        case 0: return { number: 0, name: "Select Rebalance Type" };
        case 1: return { number: 1, name: "Blacklisted Securities" };
        case 2: return { number: 2, name: "Rebalance Preview" };
        case 3: return { number: 3, name: "Crossing Trades" };
        case 4: return { number: 4, name: "Locates Management" };
        case 5: return { number: 4, name: "Locates Management" };
        case 6: return { number: 5, name: "Final Trades" };
        default: return { number: 0, name: "Select Rebalance Type" };
      }
    } else {
      // Fallback for any other type 
      switch (step) {
        case 0: return { number: 0, name: "Select Rebalance Type" };
        case 1: return { number: 1, name: "Blacklisted Securities" };
        case 2: return { number: 2, name: "Rebalance Preview" };
        case 3: return { number: 3, name: "Final Trades" };
        default: return { number: 0, name: "Select Rebalance Type" };
      }
    }
  };

  const handleStepChange = (step, type) => {
    if (step > 0) {
      setShowRestartButton(true);
    }
  };

  const handleRebalanceTypeChange = (type) => {
    setRebalanceType(type);
  };
  return (
    <>
      <Helmet>
        <title>Rebalancer | Vantage Alpha</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          background: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          minHeight: "auto",
          py: 3,
          flex: "1 1 auto",
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 1,
                }}
              >
                Rebalancer
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: 1,
                }}
              >
                Configure and manage your rebalancing strategies
              </Typography>
              {currentStep > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  >
                    Step {getStepInfo(currentStep, rebalanceType).number}:
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    {getStepInfo(currentStep, rebalanceType).name}
                  </Typography>
                </Box>
              )}
            </Box>
            {showRestartButton && (
              <Box sx={{ ml: 2, mt: 0.5 }}>
                {isLoading ? (
                  <Skeleton
                    variant="rectangular"
                    width={180}
                    height={40}
                    sx={{ borderRadius: 1 }}
                  />
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={handleRestart}
                    sx={{
                      color: "primary.main",
                      borderColor: "primary.main",
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      "&:hover": {
                        backgroundColor: "primary.light",
                        color: "white",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    Start Over
                  </Button>
                )}
              </Box>
            )}
          </Box>

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Container maxWidth="100%" sx={{ marginTop: 2, marginBottom: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ width: "100%" }}>
                    <Box>
                      {currentStep === 0 ? (
                        <RebalancerModules
                          setCurrentStep={setCurrentStep}
                          rebalancedFunds={rebalancedFunds}
                          setSelectedRebalancedFunds={setSelectedRebalancedFunds}
                          rebalancedStrategies={rebalancedStrategies}
                          setRebalancedStrategies={setRebalancedStrategies}
                          onRebalanceTypeChange={handleRebalanceTypeChange}
                          selectedSwapContract={selectedSwapContract}
                          setSelectedSwapContract={setSelectedSwapContract}
                          deltaRebalancer={deltaRebalancer}
                          setDeltaRebalancer={setDeltaRebalancer}
                          deltaValue={deltaValue}
                          setDeltaValue={setDeltaValue}
                          driftValue={driftValue}
                          setDriftValue={setDriftValue}
                        />
                      ) : rebalanceType === "swap" ? (
                        currentStep === 1 ? (
                          <SwapStep1
                            setCurrentStep={setCurrentStep}
                            contracts={selectedSwapContract}
                            setExecutionStyles={setExecutionStyles}
                          />
                        ) : currentStep === 2 ? (
                          <SwapStep2
                            setCurrentStep={setCurrentStep}
                            contracts={selectedSwapContract}
                            executionStyles={executionStyles}
                          />
                        ) : currentStep === 3 ? (
                          <SwapStep3
                            setCurrentStep={setCurrentStep}
                            contracts={selectedSwapContract}
                          />
                        ) : (
                          <></>
                        )
                      ) : currentStep === 1 ? (
                        <FundStep1
                          setCurrentStep={setCurrentStep}
                          sleeve={true}
                          rebalanceType={rebalanceType}
                          rebalancedFunds={rebalancedFunds}
                          rebalancedStrategies={rebalancedStrategies}
                          deltaRebalancer={deltaRebalancer}
                          deltaValue={deltaValue}
                          driftValue={driftValue}
                          setDriftValue={setDriftValue}
                        />
                      ) : currentStep === 2 ? (
                        <FundStep2
                          setCurrentStep={setCurrentStep}
                          sleeve={true}
                          driftValue={driftValue}
                          rebalanceType={rebalanceType}
                          rebalancedFunds={rebalancedFunds}
                          rebalancedStrategies={rebalancedStrategies}
                          deltaRebalancer={deltaRebalancer}
                        />
                      ) : currentStep === 3 ? (
                        <FundStep3
                          onPrevious={() => setCurrentStep(2)}
                          setCurrentStep={setCurrentStep}
                        />
                      ) : currentStep === 4 ? (
                        <FundStep4
                          onPrevious={() => setCurrentStep(3)}
                          setCurrentStep={setCurrentStep}
                        />
                      ) : currentStep === 5 ? (
                        <FundStep5
                          onPrevious={() => setCurrentStep(4)}
                          setCurrentStep={setCurrentStep}
                          rebalancedFunds={rebalancedFunds}
                          rebalanceType={rebalanceType}
                        />
                      ) : (
                        <FundStep6
                          rebalancedFunds={rebalancedFunds}
                          rebalanceType={rebalanceType}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default Rebalancer;
